import React, { useEffect, useState } from 'react'
import {
  AssociationCard,
  AssociationDivider,
  AssociationText,
  AssociationWrap,
  BlenderImg,
  ContactItem,
  FacebookWrap,
  FooterContainer,
  FooterLogo,
  FooterTitle,
  FooterUpCenterWrap,
  FooterUpLeftWrap,
  FooterUpWrap,
  FooterWrap,
  LinkIcon,
  LinkItem,
  LinkListExpandIcon,
  LinkListTitle,
  LinkListWrap,
  MessageUsIcon,
  MessageUsIconWrap,
  SocialMedia,
  TNPImg,
  FooterDivider,
  FooterDownWrap,
  FooterDownLinkWrap,
  FacebookRoundIcon,
  FooterDownLeftWrap,
  FooterUpDivider,
} from './atoms'
import Logo from '../logo.svg'
import intl from 'react-intl-universal'
import { FacebookIcon } from './svg/facebook-icon'
import { InstagramIcon } from './svg/instagram-icon'
import { YoutubeIcon } from './svg/youtube-icon'
import { TwitterIcon } from './svg/twitter-icon'
import { LinkedinIcon } from './svg/linkedin-icon'
import { LOCALE } from '@foxrenderfarm/internals'
import { EmailIcon2 } from './svg/email-icon'
import { ChatIcon2 } from './svg/chat-icon'
import { TelegramIcon2 } from './svg/telegram-icon'
import { WhatsAppIcon2 } from './svg/whats-app-icon'
import { FooterExpandIcon } from './svg/footer-expand-icon'
import { Link } from 'gatsby'
import { Tooltip } from '@foxrenderfarm/widgets'
import { useMedia } from 'use-media'
import { TooltipEmail } from '../../..'
import TpnBadgeSvg from './image/tpn-badge.png'
import BlenderBadgeSvg from './image/blender-badge.png'
import FacebookRoundSvg from './image/facebook-round.png'
import { TiktokIcon } from './svg/tiktok-icon'

const SocialMediaItems = [
  {
    text: 'YouTube',
    link: 'https://www.youtube.com/@FoxRenderfarm',
    target: '_blank',
    icon: <YoutubeIcon />,
  },
  {
    text: 'Linkedin',
    link: 'https://hk.linkedin.com/company/foxrenderfarm',
    target: '_blank',
    icon: <LinkedinIcon />,
  },

  {
    text: 'Facebook',
    link: 'https://www.facebook.com/foxrender/',
    target: '_blank',
    icon: <FacebookIcon />,
  },
  {
    text: 'Tiktok',
    link: 'https://www.tiktok.com/@fox_renderfarm',
    target: '_blank',
    icon: <TiktokIcon />,
  },
  {
    text: 'Instagram',
    link: 'https://www.instagram.com/foxrenderfarm/',
    target: '_blank',
    icon: <InstagramIcon />,
  },
  {
    text: 'Twitter',
    link: 'https://x.com/foxrenderfarm',
    target: '_blank',
    icon: <TwitterIcon />,
  },
]

const FOOTER_CLOUD_RENDERING_LABELS: Record<LOCALE, string> = {
  [LOCALE.EN]: 'Cloud Rendering',
  [LOCALE.ES]: 'Renderizado en la nube',
  [LOCALE.PT]: 'Renderização em nuvem',
  [LOCALE.DE]: 'Cloud-Rendering',
  [LOCALE.TR]: 'Bulut render',
  [LOCALE.JP]: 'クラウドレンダリング',
  [LOCALE.IT]: 'Rendering in cloud',
  [LOCALE.FR]: 'Rendu cloud',
  [LOCALE.RU]: 'Облачный рендеринг',
  [LOCALE.KR]: '클라우드 렌더링',
}

const messageUsList = [
  {
    key: 'email',
    link: 'service@foxrenderfarm.com',
    esLink: 'service@foxrenderfarm.com',
    icon: <EmailIcon2 />,
    ariaLabel: 'email',
  },
  {
    key: 'whatsapp',
    link: 'https://api.whatsapp.com/send/?phone=8618124758259&text=Hello.May%20i%20help%20you?',
    esLink: 'https://api.whatsapp.com/send/?phone=8618124758259&text=Hello.May%20i%20help%20you?',
    icon: <WhatsAppIcon2 />,
    ariaLabel: 'whatsapp',
  },
  {
    key: 'chat',
    icon: <ChatIcon2 />,
    ariaLabel: 'chat',
    clickHandle: () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ;(window as any).ssq.push('chatOpen')
    },
  },
  {
    key: 'telegram',
    icon: <TelegramIcon2 />,
    ariaLabel: 'telegram',
    hoverContent: '+86 18002563139',
    className: 'telegram',
  },
]

interface Props {
  locale: LOCALE
  origin: string
}

interface LinkItemConfig {
  text: string
  link: string
  useGatsbyLink?: boolean
  useAnchor?: boolean
  isHide?: boolean
  key?: string
}

interface FooterColumnProps {
  title: string
  items: LinkItemConfig[]
  locale: LOCALE
  isMobile: boolean
  isOpen: boolean
  onToggle: () => void
}

const FooterLinkColumn = ({
  title,
  items,
  locale,
  isMobile,
  isOpen,
  onToggle,
}: FooterColumnProps) => {
  return (
    <LinkListWrap>
      <LinkListTitle onClick={isMobile ? onToggle : undefined}>
        {title}
        {isMobile && (
          <LinkListExpandIcon $isOpen={isOpen}>
            <FooterExpandIcon />
          </LinkListExpandIcon>
        )}
      </LinkListTitle>
      {(!isMobile || isOpen) &&
        items.map((item) =>
          item.isHide ? null : (
            <LinkItem
              as={item.useAnchor || !item.useGatsbyLink ? 'a' : Link}
              key={item.key ?? item.text}
              to={item.link}
              href={item.link}
            >
              {item.text}
            </LinkItem>
          ),
        )}
      {title === intl.get('index.footer.company') && locale === LOCALE.JP && (
        <LinkItem as='a' href='/jp/tradelaw.html'>
          特定商取引法に基づく表示
        </LinkItem>
      )}
    </LinkListWrap>
  )
}

const BusinessContactColumn = ({
  isMobile,
  isOpen,
  onToggle,
}: {
  isMobile: boolean
  isOpen: boolean
  onToggle: () => void
}) => (
  <LinkListWrap>
    <LinkListTitle onClick={isMobile ? onToggle : undefined}>
      {intl.get('index.footer.business')}
      {isMobile && (
        <LinkListExpandIcon $isOpen={isOpen}>
          <FooterExpandIcon />
        </LinkListExpandIcon>
      )}
    </LinkListTitle>
    {(!isMobile || isOpen) && (
      <>
        <ContactItem>
          <p>{intl.get('index.footer.agentContact')}</p>
          <p>{intl.get('index.footer.agentEmail')}</p>
        </ContactItem>
        <ContactItem>
          <p>{intl.get('index.footer.marketingContact')}</p>
          <p>{intl.get('index.footer.marketingEmail')}</p>
        </ContactItem>
      </>
    )}
  </LinkListWrap>
)

const MessageUsColumn = ({
  locale,
  isMobile,
  showEmail,
  setShowEmail,
  isOpen,
  onToggle,
}: {
  locale: LOCALE
  isMobile: boolean
  showEmail: boolean
  setShowEmail: (value: boolean) => void
  isOpen: boolean
  onToggle: () => void
}) => (
  <LinkListWrap>
    <LinkListTitle onClick={isMobile ? onToggle : undefined}>
      {intl.get('index.footer.messageUs')}
      {isMobile && (
        <LinkListExpandIcon $isOpen={isOpen}>
          <FooterExpandIcon />
        </LinkListExpandIcon>
      )}
    </LinkListTitle>
    {(!isMobile || isOpen) && (
      <MessageUsIconWrap>
        {messageUsList.map((item) =>
          item.key === 'email' || item.hoverContent ? (
            <Tooltip
              key={item.key}
              content={
                item.key === 'email' ? (
                  <TooltipEmail email='service@foxrenderfarm.com' />
                ) : (
                  <span style={{ color: '#fff' }}>{item.hoverContent}</span>
                )
              }
              handleShow={(v) => setShowEmail(v)}
              background='#1e1e1e'
            >
              <MessageUsIcon
                as={!isMobile || showEmail ? 'a' : 'div'}
                key={item.key}
                href={item.key === 'email' ? `mailto:${item.link}` : undefined}
                aria-label={!isMobile || showEmail ? item.ariaLabel : undefined}
                className={item.className}
              >
                {item.icon}
              </MessageUsIcon>
            </Tooltip>
          ) : (
            <MessageUsIcon
              key={item.key}
              href={locale === LOCALE.ES ? item.esLink : item.link}
              aria-label={item.ariaLabel}
              rel='noopener noreferrer nofollow'
              onClick={item.clickHandle}
              className={item.className}
            >
              {item.icon}
            </MessageUsIcon>
          ),
        )}
      </MessageUsIconWrap>
    )}
  </LinkListWrap>
)

export const Footer = ({ locale, origin }: Props) => {
  const isMobile = useMedia({ maxWidth: 600 })
  const [showEmail, setShowEmail] = useState(false)
  const [isProductOpen, setIsProductOpen] = useState(false)
  const [isSupportOpen, setIsSupportOpen] = useState(false)
  const [isMessageOpen, setIsMessageOpen] = useState(false)
  const [isProgramOpen, setIsProgramOpen] = useState(false)
  const [isBusinessOpen, setIsBusinessOpen] = useState(false)
  const [isCompanyOpen, setIsCompanyOpen] = useState(false)
  const pathLocale = locale === LOCALE.EN ? '' : `/${locale}`

  const companyList = [
    {
      text: intl.get('index.footer.company.cloud.rendering'),
      link: `${pathLocale}/cloud-rendering.html`,
      useGatsbyLink: process.env.GATSBY_PACKAGE_NAME === 'main',
    },
    {
      text: intl.get('index.footer.company.pricing'),
      link: `${pathLocale}/pricing.html`,
      useGatsbyLink: process.env.GATSBY_PACKAGE_NAME === 'main',
    },
    {
      text: intl.get('index.footer.company.customer'),
      link: `${pathLocale}/client-works.html`,
      useGatsbyLink: process.env.GATSBY_PACKAGE_NAME === 'main',
    },
    {
      text: intl.get('index.footer.company.about.us'),
      link: `${pathLocale}/about.html`,
      key: 'about',
      useGatsbyLink: process.env.GATSBY_PACKAGE_NAME === 'main',
    },
    {
      text: intl.get('index.header.about.global.resellers'),
      link: `${pathLocale}/global-partner.html`,
      key: 'global-partner',
      useGatsbyLink: process.env.GATSBY_PACKAGE_NAME === 'main',
    },
    {
      text: intl.get('index.footer.company.partners'),
      link: `${pathLocale}/partner.html`,
      key: 'partners',
      useGatsbyLink: process.env.GATSBY_PACKAGE_NAME === 'main',
    },
    {
      isHide: locale !== LOCALE.EN,
      text: intl.get('index.footer.company.animation.booster'),
      link: `/animation-workflow-booster.html`,
      useGatsbyLink: process.env.GATSBY_PACKAGE_NAME === 'main',
    },
    {
      isHide: locale !== LOCALE.EN,
      key: 'news',
      text: intl.get('index.footer.company.news'),
      useAnchor: true,
      link: `${origin}/news/main/`,
      useGatsbyLink: process.env.GATSBY_PACKAGE_NAME === 'news',
    },
  ]

  const companyListForRender = companyList.filter(
    (item) =>
      item.key === 'about' ||
      item.key === 'global-partner' ||
      item.key === 'partners' ||
      item.key === 'news',
  )

  const programList = [
    {
      text: intl.get('index.footer.program.referral'),
      link: `${pathLocale}/invite.html`,
      useGatsbyLink: process.env.GATSBY_PACKAGE_NAME === 'main',
    },
    {
      text: intl.get('index.footer.program.goCloud'),
      link: `${pathLocale}/gocloud-program.html`,
      useGatsbyLink: process.env.GATSBY_PACKAGE_NAME === 'main',
    },
    {
      text: intl.get('index.footer.program.goCreation'),
      link: `${pathLocale}/gocreation-program.html`,
      useGatsbyLink: process.env.GATSBY_PACKAGE_NAME === 'main',
    },
    {
      text: intl.get('index.footer.program.goSpark'),
      link: `${pathLocale}/free-render-farm.html`,
      useGatsbyLink: process.env.GATSBY_PACKAGE_NAME === 'main',
    },
    // {
    //   text: intl.get('index.footer.program.FGT.community'),
    //   link: '/fgt-community.html',
    //   useGatsbyLink: process.env.GATSBY_PACKAGE_NAME === 'main',
    // },
  ]

  const productList: LinkItemConfig[] = [
    {
      text: FOOTER_CLOUD_RENDERING_LABELS[locale],
      link: `${pathLocale}/cloud-rendering.html`,
      useGatsbyLink: process.env.GATSBY_PACKAGE_NAME === 'main',
    },
    {
      isHide: locale !== LOCALE.EN,
      text: 'Animation Booster',
      link: `/animation-workflow-booster.html`,
      useGatsbyLink: process.env.GATSBY_PACKAGE_NAME === 'main',
    },
    {
      text: 'Raytool',
      useAnchor: true,
      link: `${origin}/raytools.html`,
      useGatsbyLink: process.env.GATSBY_PACKAGE_NAME === 'legacy-main',
    },
  ]

  const supportList: LinkItemConfig[] = [
    {
      text: intl.get('index.footer.support.download'),
      link: `${pathLocale}/download.html`,
      useGatsbyLink: process.env.GATSBY_PACKAGE_NAME === 'main',
    },
    {
      text: intl.get('index.footer.support.help'),
      useAnchor: true,
      link: `${origin}/support/`,
      useGatsbyLink: process.env.GATSBY_PACKAGE_NAME === 'support',
    },
    {
      text: intl.get('index.footer.support.blog'),
      useAnchor: true,
      link: `${origin}/share/index/`,
      useGatsbyLink: process.env.GATSBY_PACKAGE_NAME === 'share',
    },
  ]

  useEffect(() => {
    if (isMobile) {
      setIsProductOpen(false)
      setIsSupportOpen(false)
      setIsMessageOpen(false)
      setIsProgramOpen(false)
      setIsBusinessOpen(false)
      setIsCompanyOpen(false)
    } else {
      setIsProductOpen(true)
      setIsSupportOpen(true)
      setIsMessageOpen(true)
      setIsProgramOpen(true)
      setIsBusinessOpen(true)
      setIsCompanyOpen(true)
    }
  }, [isMobile])

  return (
    <FooterContainer>
      <FooterWrap>
        <FooterUpWrap>
          <FooterUpLeftWrap>
            <FooterLogo to={locale === LOCALE.EN ? '/' : `/${locale}`}>
              <img src={Logo} alt='Fox Renderfarm Cloud Render Farm Service' loading='lazy' />
            </FooterLogo>
            <FooterTitle>{intl.get('index.footer.title').toUpperCase()}</FooterTitle>
            <SocialMedia>
              {SocialMediaItems.map((item) => (
                <LinkIcon
                  key={item.text}
                  href={item.link}
                  target={item.target}
                  rel='noopener noreferrer nofollow'
                  aria-label={item.text}
                >
                  {item.icon}
                </LinkIcon>
              ))}
            </SocialMedia>
          </FooterUpLeftWrap>
          <FooterUpDivider />
          <FooterUpCenterWrap>
            <LinkListWrap>
              <LinkListTitle
                onClick={() => {
                  if (isMobile) {
                    setIsProductOpen((value) => !value)
                  }
                }}
              >
                {intl.get('index.header.products')}
                {isMobile && (
                  <LinkListExpandIcon $isOpen={isProductOpen}>
                    <FooterExpandIcon />
                  </LinkListExpandIcon>
                )}
              </LinkListTitle>
              {(!isMobile || isProductOpen) &&
                productList.map((item) =>
                  item.isHide ? null : (
                    <LinkItem
                      as={item.useAnchor || !item.useGatsbyLink ? 'a' : Link}
                      key={item.text}
                      to={item.link}
                      href={item.link}
                    >
                      {item.text}
                    </LinkItem>
                  ),
                )}
            </LinkListWrap>
            <LinkListWrap>
              <LinkListTitle
                onClick={() => {
                  if (isMobile) {
                    setIsSupportOpen((value) => !value)
                  }
                }}
              >
                {intl.get('index.footer.support')}
                {isMobile && (
                  <LinkListExpandIcon $isOpen={isSupportOpen}>
                    <FooterExpandIcon />
                  </LinkListExpandIcon>
                )}
              </LinkListTitle>
              {(!isMobile || isSupportOpen) &&
                supportList.map((item) => (
                  <LinkItem
                    as={item.useAnchor || !item.useGatsbyLink ? 'a' : Link}
                    key={item.text}
                    to={item.link}
                    href={item.link}
                  >
                    {item.text}
                  </LinkItem>
                ))}
            </LinkListWrap>
            <MessageUsColumn
              locale={locale}
              isMobile={isMobile}
              showEmail={showEmail}
              setShowEmail={setShowEmail}
              isOpen={isMessageOpen}
              onToggle={() => {
                if (isMobile) {
                  setIsMessageOpen((value) => !value)
                }
              }}
            />
            <LinkListWrap>
              <LinkListTitle
                onClick={() => {
                  if (isMobile) {
                    setIsProgramOpen((value) => !value)
                  }
                }}
              >
                {intl.get('index.footer.program')}
                {isMobile && (
                  <LinkListExpandIcon $isOpen={isProgramOpen}>
                    <FooterExpandIcon />
                  </LinkListExpandIcon>
                )}
              </LinkListTitle>
              {(!isMobile || isProgramOpen) &&
                programList.map((item) => (
                  <LinkItem
                    as={item.useGatsbyLink ? Link : 'a'}
                    key={item.text}
                    to={item.link}
                    href={item.link}
                  >
                    {item.text}
                  </LinkItem>
                ))}
            </LinkListWrap>
            <FooterLinkColumn
              title={intl.get('index.footer.company')}
              items={companyListForRender}
              locale={locale}
              isMobile={isMobile}
              isOpen={isCompanyOpen}
              onToggle={() => {
                if (isMobile) {
                  setIsCompanyOpen((value) => !value)
                }
              }}
            />
            <BusinessContactColumn
              isMobile={isMobile}
              isOpen={isBusinessOpen}
              onToggle={() => {
                if (isMobile) {
                  setIsBusinessOpen((value) => !value)
                }
              }}
            />
          </FooterUpCenterWrap>
        </FooterUpWrap>
        <FooterDivider />
        <FooterDownWrap>
          <FooterDownLeftWrap>
            Copyright © 2026 FoxRenderfarm.com. All Rights Reserved.
            <FooterDownLinkWrap>
              <LinkItem as='a' href={`${origin}/term-of-service.html`}>
                {intl.get('index.footer.terms')}
              </LinkItem>
              <LinkItem as='a' href={`${origin}/privacy.html`}>
                {intl.get('index.footer.privacy')}
              </LinkItem>
            </FooterDownLinkWrap>
          </FooterDownLeftWrap>
          <AssociationWrap>
            <AssociationCard className='tpn'>
              <TNPImg loading='lazy' src={TpnBadgeSvg} alt='Trusted Partner Network' />
              <AssociationDivider />
              <AssociationText>
                <div>Trusted Partner</div>
                <div>Network</div>
              </AssociationText>
            </AssociationCard>
            <AssociationCard className='blender'>
              <BlenderImg
                loading='lazy'
                src={BlenderBadgeSvg}
                alt='Blender Development Fund Member'
              />
              <AssociationDivider />
              <AssociationText>
                <div>Blender Development</div>
                <div>Fund Member</div>
              </AssociationText>
            </AssociationCard>
            <FacebookWrap
              href='https://www.facebook.com/foxrender/reviews/'
              target='_blank'
              rel='noopener noreferrer nofollow'
            >
              <FacebookRoundIcon src={FacebookRoundSvg} loading='lazy' alt='facebook score 4.9' />
              <AssociationDivider />
              <AssociationText>
                <div>Facebook</div>
                <div>Customer Reviews</div>
              </AssociationText>
            </FacebookWrap>
          </AssociationWrap>
        </FooterDownWrap>
      </FooterWrap>
    </FooterContainer>
  )
}
